import { useToast } from 'components/ui/toast';
import { Formik, FormikErrors } from 'formik';
import { useEffect, useRef } from 'react';
import { UploadState } from 'utils/fileUtils';
import { removeTagsFromString } from '../helpers/utils';
import { useSendMessage } from '../hooks/conversations-hooks';
import { MessageBox } from '../shared/message-box';

export interface FormMessageAttachment {
	file: File;
	key: string;
	uploadState: UploadState;
	uploadProgress: number[];
	uploadToken?: string;
}

export interface NewMessageFormValues {
	requestedMessageId?: string | undefined;
	messageText: string;
	messageAttachments: FormMessageAttachment[];
}

interface ConversationMessageBoxProps {
	onSuccess?: () => void;
}

/**
 * Wrapper around MessageBox that handles sending a new message
 */
export const ConversationMessageBox = ({ onSuccess }: ConversationMessageBoxProps) => {
	let messageLoadingRef = useRef(false);
	const toast = useToast();
	const initialValues: NewMessageFormValues = {
		messageText: '',
		messageAttachments: [],
	};

	const validate = (values: NewMessageFormValues) => {
		const errors: FormikErrors<NewMessageFormValues> = {};
		if (!removeTagsFromString(values.messageText)) errors.messageText = 'Required Field';
		if (values.messageAttachments.some((a) => a.uploadState !== UploadState.Success))
			errors.messageAttachments = 'Upload in progress';
		return errors;
	};

	const { sendMessage, loading: sendMessageLoading } = useSendMessage();
	const onSubmit = async (values: NewMessageFormValues) => {
		const { requestedMessageId, messageText, messageAttachments } = values;

		setTimeout(() => {
			if (messageLoadingRef.current) {
				toast.push(
					"This message is taking a while to send. If you've attached a large file please be patient while we process it. Feel free to leave this page. The message will appear in the conversation when processing is complete.",
					{
						variant: 'success',
					}
				);
			}
		}, 5000);

		const success = await sendMessage({
			requestedMessageId: requestedMessageId,
			messageText,
			attachmentUploadTokens: messageAttachments
				.filter((a) => a.uploadToken != null && a.uploadToken != undefined)
				.map((a) => a.uploadToken!),
		});

		if (success) onSuccess?.();
	};

	useEffect(() => {
		messageLoadingRef.current = sendMessageLoading;
	}, [sendMessageLoading]);

	return (
		<Formik<NewMessageFormValues>
			onSubmit={onSubmit}
			initialValues={initialValues}
			validate={validate}>
			<MessageBox />
		</Formik>
	);
};
